/**
 * Safety hub + 5 priority sub-pages — Sprint D (Feb 2026)
 * Priority order per spec: emergency → report → red-flags → tips → share-date.
 * The 4 non-priority routes (healthy-dating, verification, credibility,
 * ai-assistant) render a "coming soon" placeholder for now.
 */
import { Link, useNavigate } from 'react-router-dom';
import {
  ShieldCheck, AlertTriangle, Flag, Lightbulb, MapPin, Heart, BadgeCheck,
  Award, Bot, PhoneCall, Users, Eye, Lock, MessageCircle, ChevronRight,
  HeartHandshake, HandMetal, Leaf, TrendingDown, Hourglass, Sparkles,
  CheckCircle2, XCircle, Star, CalendarClock, Clock, AlertCircle,
  GraduationCap, HelpCircle, ShieldHalf,
} from 'lucide-react';
import {
  LegalPageShell, HeroCard, Section, P, Bullet, LegalFooter, CalloutCard,
  Callout, TopicCard,
} from '@/components/legal/LegalPrimitives';

/* ------------------------------------------------------------ */
/* Hub                                                          */
/* ------------------------------------------------------------ */
const HUB_CARDS = [
  { to: '/safety/emergency',   icon: AlertTriangle, tint: '#FEF2F2', color: '#B91C1C', title: 'Emergency contacts',       desc: 'What to do if you feel unsafe right now' },
  { to: '/safety/report',      icon: Flag,          tint: '#FFFBEB', color: '#B45309', title: 'How to report a user',     desc: 'Step-by-step reporting walkthrough' },
  { to: '/safety/red-flags',   icon: AlertTriangle, tint: '#FEF2F2', color: '#B91C1C', title: 'Red-flag warning signs',   desc: 'What patterns to watch for' },
  { to: '/safety/tips',        icon: Lightbulb,     tint: '#FFFBEB', color: '#B45309', title: 'General safety tips',      desc: 'Everyday habits that keep you safe' },
  { to: '/safety/share-date',  icon: MapPin,        tint: '#EFF6FF', color: '#1D4ED8', title: 'Safe Meet check-in',       desc: 'Share your date plan with a trusted contact' },
  { to: '/safety/healthy-dating',  icon: Heart,      tint: '#F0FDF4', color: '#166534', title: 'Healthy dating signals',   desc: 'What a good match looks like' },
  { to: '/safety/verification',    icon: BadgeCheck, tint: '#EFF6FF', color: '#1D4ED8', title: 'Verify your profile',      desc: 'Photo & phone verification walkthrough' },
  { to: '/safety/credibility',     icon: Award,      tint: '#F3E8FF', color: '#7E22CE', title: 'Credibility tiers',        desc: 'How your reputation works' },
  { to: '/safety/ai-assistant',    icon: Bot,        tint: '#F0FDF4', color: '#166534', title: 'AI safety assistant',      desc: 'Get instant guidance from our AI' },
];

export function SafetyHub() {
  return (
    <LegalPageShell title="Safety Center">
      <HeroCard icon={ShieldCheck} title="Meet to Keep Safety Center" />

      <CalloutCard tone="red" label="In danger right now?">
        Call your local emergency number (911 in the US) immediately, then use the report button to alert us.
      </CalloutCard>

      <p className="text-[11px] font-[700] tracking-[0.12em] uppercase text-[#6B7280] mt-4 mb-2.5">
        Browse safety topics
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="safety-hub-grid">
        {HUB_CARDS.map((c) => (
          <Link
            key={c.to}
            to={c.to}
            data-testid={`safety-card-${c.to.split('/').pop()}`}
            className="flex items-center gap-3 rounded-2xl p-4 hover:shadow-[0_2px_8px_rgba(0,0,0,0.06)] transition-shadow"
            style={{ background: '#FFFFFF', border: '1px solid rgba(15,15,16,0.06)' }}
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: c.tint }}
            >
              <c.icon size={18} style={{ color: c.color }} strokeWidth={2.2} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[14px] font-[800] text-[#0F0F10] tracking-[-0.2px]">{c.title}</p>
              <p className="text-[12px] text-[#6B7280] font-[500] mt-0.5 truncate">{c.desc}</p>
            </div>
            <ChevronRight size={16} className="text-[#9CA3AF]"/>
          </Link>
        ))}
      </div>

      <LegalFooter />
    </LegalPageShell>
  );
}

/* ------------------------------------------------------------ */
/* Priority page 1 — Emergency                                  */
/* ------------------------------------------------------------ */
export function SafetyEmergency() {
  return (
    <LegalPageShell title="Emergency" backTo="/safety">
      <HeroCard icon={PhoneCall} title="Get help now" />

      <CalloutCard tone="red" label="Life-threatening emergency">
        Call your local emergency number immediately.
      </CalloutCard>

      <Section number={1} title="United States & Canada">
        <P>Dial <b>911</b> for police, fire, or medical emergencies.</P>
        <P>National Domestic Violence Hotline: <b>1-800-799-7233</b> (24/7).</P>
        <P>Suicide &amp; Crisis Lifeline: <b>988</b> (call or text).</P>
        <P>National Sexual Assault Hotline (RAINN): <b>1-800-656-4673</b>.</P>
      </Section>

      <Section number={2} title="United Kingdom">
        <P>Emergency: <b>999</b> · Non-emergency police: <b>101</b>.</P>
        <P>Refuge Domestic Abuse Helpline: <b>0808 2000 247</b>.</P>
      </Section>

      <Section number={3} title="European Union">
        <P>Emergency: <b>112</b> (works across all EU member states).</P>
      </Section>

      <Section number={4} title="Australia">
        <P>Emergency: <b>000</b>. 1800RESPECT: <b>1800 737 732</b>.</P>
      </Section>

      <Section number={5} title="If you're on a date right now">
        <Bullet>Excuse yourself to the restroom and message a trusted contact.</Bullet>
        <Bullet>Ask staff at the venue for help — they're trained for this.</Bullet>
        <Bullet>Order an "Angel Shot" at the bar (universal safety code word in most US venues).</Bullet>
        <Bullet>Use the "Safe Meet check-in" feature to alert your emergency contact.</Bullet>
      </Section>

      <Section number={6} title="After a bad experience">
        <P>Report the user in-app as soon as you're safe. Include screenshots, phone numbers, and any details you can remember. Our Trust &amp; Safety team reviews every report.</P>
        <P>If a crime occurred, file a police report and share the incident number with us at <a href="mailto:safety@meettokeep.com" className="underline text-[#B91C1C]">safety@meettokeep.com</a>.</P>
      </Section>

      <LegalFooter />
    </LegalPageShell>
  );
}

/* ------------------------------------------------------------ */
/* Priority page 2 — Report                                     */
/* ------------------------------------------------------------ */
export function SafetyReport() {
  const navigate = useNavigate();
  return (
    <LegalPageShell title="How to report" backTo="/safety">
      <HeroCard icon={Flag} title="How to report a user" />

      <CalloutCard tone="amber" label="Every report is reviewed">
        Our Trust &amp; Safety team reviews every report within 24 hours. Serious reports are escalated immediately.
      </CalloutCard>

      <Section number={1} title="From a profile">
        <Bullet>Open the profile from Encounters or Matches.</Bullet>
        <Bullet>Tap the <b>Report</b> button — it's at the bottom of the profile (neutral gray, next to Block).</Bullet>
        <Bullet>Choose a reason: harassment, spam, fake profile, inappropriate content, safety concern, or other.</Bullet>
        <Bullet>Add optional details — the more context you give, the faster we can act.</Bullet>
        <Bullet>Submit. The reported user won't be notified.</Bullet>
      </Section>

      <Section number={2} title="From a chat">
        <Bullet>Open the chat, tap the info icon in the top-right, then <b>Report user</b>.</Bullet>
        <Bullet>Screenshots of the offending message are automatically included.</Bullet>
      </Section>

      <Section number={3} title="What happens next">
        <P>Reports are triaged by severity. High-severity issues (threats, illegal activity, minors) are escalated within 1 hour.</P>
        <P>Repeat offenders and serious violations lead to permanent bans, shadow-throttling, and — where relevant — reports to law enforcement.</P>
        <P>You will not receive a play-by-play of the outcome, but you can always block the user to remove them from your feed entirely.</P>
      </Section>

      <Section number={4} title="What to include">
        <Bullet>User's display name or profile URL if you have it.</Bullet>
        <Bullet>Screenshots of messages, photos, or profile details.</Bullet>
        <Bullet>Approximate date &amp; time of the incident.</Bullet>
        <Bullet>Whether you've met in person.</Bullet>
      </Section>

      <div className="mt-6 flex gap-2">
        <button
          onClick={() => navigate('/discover')}
          data-testid="safety-report-go-discover"
          className="flex-1 rounded-full bg-[#0F0F10] text-white text-[14px] font-[700] py-3"
        >
          Go to Encounters
        </button>
        <a
          href="mailto:safety@meettokeep.com"
          data-testid="safety-report-email"
          className="flex-1 rounded-full text-center text-[14px] font-[700] py-3"
          style={{ background: '#FFFFFF', border: '1px solid rgba(15,15,16,0.12)', color: '#0F0F10' }}
        >
          Email T&amp;S team
        </a>
      </div>

      <LegalFooter />
    </LegalPageShell>
  );
}

/* ------------------------------------------------------------ */
/* Priority page 3 — Red flags                                  */
/* ------------------------------------------------------------ */
export function SafetyRedFlags() {
  return (
    <LegalPageShell title="Red flags" backTo="/safety">
      <HeroCard icon={AlertTriangle} title="Red-flag warning signs" />

      <CalloutCard tone="red" label="Trust your gut">
        If something feels off — it usually is. You never owe anyone a reply, another date, or an explanation.
      </CalloutCard>

      <Section number={1} title="Love-bombing">
        <P>Excessive compliments, "I've never felt this way before," and pressure to commit or become exclusive within days. Real intimacy takes time.</P>
      </Section>

      <Section number={2} title="Money talk, early">
        <P>Any request for money, gift cards, crypto, or "help with an emergency" from someone you haven't met in person is a scam. Report and block immediately.</P>
      </Section>

      <Section number={3} title="Refusal to video chat">
        <P>If they always have an excuse to skip video calls — camera broken, bad WiFi, "shy" — the profile may be fake. Try a spontaneous video call and see how they respond.</P>
      </Section>

      <Section number={4} title="Only wants to move off-platform immediately">
        <P>Pressure to switch to WhatsApp / Telegram / Signal / email in the first message is a classic scammer move. It gets you outside our safety tools. Keep chatting inside Meet to Keep until trust is built.</P>
      </Section>

      <Section number={5} title="Inconsistent details">
        <P>Their age, job, city, or photos don't match up between the profile and chat. Screenshot examples and report.</P>
      </Section>

      <Section number={6} title="Pushiness about meeting alone or in private">
        <P>A safe date meets in a public place. If they insist on a hotel, a private residence, or "somewhere quiet" for a first meet — pass. Suggest a coffee, café, or park instead.</P>
      </Section>

      <Section number={7} title="Explosive reactions">
        <P>Rage, guilt-tripping, or silent-treatment tactics when you set a boundary. Real partners respect boundaries the first time.</P>
      </Section>

      <Section number={8} title="Sexual pressure">
        <P>Explicit photos, unwanted sexting, or pressure for sex before you're ready — including <i>after</i> a first date — is never okay. Block, report, and reach out to <a href="mailto:safety@meettokeep.com" className="underline text-[#B91C1C]">safety@meettokeep.com</a> if it escalates.</P>
      </Section>

      <LegalFooter />
    </LegalPageShell>
  );
}

/* ------------------------------------------------------------ */
/* Priority page 4 — Tips                                       */
/* ------------------------------------------------------------ */
export function SafetyTips() {
  return (
    <LegalPageShell title="Safety tips" backTo="/safety">
      <HeroCard icon={Lightbulb} title="General safety tips" />

      <Section number={1} title="Before the first meet">
        <Bullet>Video chat at least once. It's a five-minute vibe check that catches most catfish.</Bullet>
        <Bullet>Search their name and reverse-image-search their photos.</Bullet>
        <Bullet>Set the first meet in a busy, public place — coffee shops, breweries, parks in daylight.</Bullet>
        <Bullet>Tell a friend where you're going, when, and with whom. Use the <b>Safe Meet check-in</b> feature.</Bullet>
        <Bullet>Drive yourself or take a rideshare — don't accept a pick-up from someone you've never met.</Bullet>
      </Section>

      <Section number={2} title="During the meet">
        <Bullet>Order and watch your own drinks. Never leave a drink unattended.</Bullet>
        <Bullet>Keep your phone charged and on you.</Bullet>
        <Bullet>Trust your gut — you can leave at any time, for any reason, without explanation.</Bullet>
        <Bullet>Don't share your home address, workplace, or exact daily schedule until trust is built.</Bullet>
      </Section>

      <Section number={3} title="Online safety">
        <Bullet>Use a photo of just you (no kids, no license plates, no house numbers).</Bullet>
        <Bullet>Turn off location metadata on photos you upload.</Bullet>
        <Bullet>Don't reuse your dating photos on public profiles — it defeats reverse image search.</Bullet>
        <Bullet>Choose a unique password for your account and enable 2FA where offered.</Bullet>
      </Section>

      <Section number={4} title="Protect your privacy">
        <Bullet>Never share your SSN, bank details, or ID with a match.</Bullet>
        <Bullet>Consider a Google Voice number for your first phone calls off-app.</Bullet>
        <Bullet>Keep social media handles private until you've met safely a few times.</Bullet>
      </Section>

      <Section number={5} title="After the meet">
        <Bullet>Check in with a friend so they know you got home safe.</Bullet>
        <Bullet>Log any red flags in your notes app in case you need them later.</Bullet>
        <Bullet>If something felt off, report it — even if you're not 100% sure. Patterns matter.</Bullet>
      </Section>

      <LegalFooter />
    </LegalPageShell>
  );
}

/* ------------------------------------------------------------ */
/* Priority page 5 — Share Date (Safe Meet)                     */
/* ------------------------------------------------------------ */
export function SafetyShareDate() {
  const navigate = useNavigate();
  return (
    <LegalPageShell title="Safe Meet" backTo="/safety">
      <HeroCard icon={MapPin} title="Share your date plan" />

      <CalloutCard tone="blue" label="How it works">
        Safe Meet lets you share your date plan with a trusted contact. If you don't check in by a chosen time, they get a nudge — and can escalate if needed.
      </CalloutCard>

      <Section number={1} title="Set up your Safe Meet contact">
        <Bullet>Open Settings → Safe Meet.</Bullet>
        <Bullet>Add a trusted contact's name and phone or email.</Bullet>
        <Bullet>Optional: pre-approve them to see your live location during a date.</Bullet>
      </Section>

      <Section number={2} title="Start a check-in">
        <Bullet>Before you head out, tap "New check-in" on the Safe Meet screen.</Bullet>
        <Bullet>Enter the venue, the match's first name, and your expected end time.</Bullet>
        <Bullet>Your contact gets a text/email with the plan.</Bullet>
      </Section>

      <Section number={3} title="During your date">
        <Bullet>The countdown runs in the background. You'll get a nudge 15 minutes before the end time.</Bullet>
        <Bullet>Tap "I'm safe" to end the check-in early. Tap "Extend" if the date's going well.</Bullet>
        <Bullet>Tap "Need help" for a one-tap escalation — your contact gets a "please check on me" alert.</Bullet>
      </Section>

      <Section number={4} title="If you don't check in">
        <Bullet>Your contact gets an escalated alert with the venue and match info.</Bullet>
        <Bullet>Our team is notified in parallel and may reach out to authorities in extreme cases.</Bullet>
      </Section>

      <Section number={5} title="Your privacy">
        <P>Location data is only visible to the contact you choose, and only during the active check-in window. We never sell or share location data with third parties.</P>
      </Section>

      <button
        onClick={() => navigate('/safe-meet')}
        data-testid="safety-share-date-cta"
        className="w-full rounded-full text-white font-[900] text-[15px] py-3.5 mt-6"
        style={{
          background: 'linear-gradient(135deg, #DC2626 0%, #7E22CE 50%, #1E4FD8 100%)',
          boxShadow: '0 6px 20px -4px rgba(220,38,38,0.45)',
        }}
      >
        Set up Safe Meet
      </button>

      <LegalFooter />
    </LegalPageShell>
  );
}

/* ------------------------------------------------------------ */
/* 6. Healthy Dating Tips                                       */
/* ------------------------------------------------------------ */
export function SafetyHealthyDating() {
  const chips = [
    { label: 'Communicate',   icon: MessageCircle, grad: 'linear-gradient(135deg, #EC4899, #DC2626)' },
    { label: 'Boundaries',    icon: HandMetal,     grad: 'linear-gradient(135deg, #DC2626, #7E22CE)' },
    { label: 'Respect',       icon: Heart,         grad: 'linear-gradient(135deg, #7E22CE, #1E4FD8)' },
    { label: 'Take your time',icon: Leaf,          grad: 'linear-gradient(135deg, #16A34A, #0EA5E9)' },
  ];
  return (
    <LegalPageShell title="Healthy dating" backTo="/safety">
      <HeroCard icon={HeartHandshake} title="Healthy Dating Tips" />
      <p className="text-center text-[13px] font-[600] text-[#6B7280] -mt-4 mb-4">
        Intentional, respectful, and comfortable dating — by design.
      </p>

      {/* Chip row */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="hd-chip-row">
        {chips.map((c) => (
          <span
            key={c.label}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-white text-[12px] font-[800]"
            style={{ background: c.grad, boxShadow: '0 4px 10px -2px rgba(0,0,0,0.12)' }}
          >
            <c.icon size={13} strokeWidth={2.4} />
            {c.label}
          </span>
        ))}
      </div>

      <P>Great matches aren&rsquo;t just &ldquo;safe&rdquo; &mdash; they&rsquo;re healthy. Below are the principles that separate connection from control.</P>

      <TopicCard number={1} icon={Eye} gradient="linear-gradient(135deg, #DC2626, #7E22CE)" title="How to Spot Manipulation">
        <P>Manipulation often disguises itself as passion, care, or urgency. Watch for:</P>
        <Bullet><b>Love bombing:</b> intense affection, gifts, or &ldquo;you&rsquo;re the one&rdquo; far too early.</Bullet>
        <Bullet><b>Guilt-tripping:</b> &ldquo;After everything I&rsquo;ve done for you&hellip;&rdquo;</Bullet>
        <Bullet><b>Gaslighting:</b> denying things you clearly remember or feel.</Bullet>
        <Bullet><b>Isolation:</b> criticizing your friends, family, or hobbies.</Bullet>
        <Bullet><b>Silent treatment:</b> withholding communication as punishment.</Bullet>
        <Bullet><b>Moving fast:</b> pushing for exclusivity, cohabiting, or money quickly.</Bullet>
        <Callout tone="info">Healthy people ask. Unhealthy people pressure.</Callout>
      </TopicCard>

      <TopicCard number={2} icon={HandMetal} gradient="linear-gradient(135deg, #F97316, #DC2626)" title="Setting Boundaries">
        <P>Boundaries aren&rsquo;t rules for the other person &mdash; they&rsquo;re commitments to yourself about what you will accept.</P>
        <p className="text-[13px] font-[800] mt-3 mb-1" style={{ color: '#166534' }}>Good boundaries sound like:</p>
        <Bullet positive>&ldquo;I&rsquo;m not ready to share that yet.&rdquo;</Bullet>
        <Bullet positive>&ldquo;I don&rsquo;t give my address to people I&rsquo;ve just met.&rdquo;</Bullet>
        <Bullet positive>&ldquo;I&rsquo;m going home now.&rdquo;</Bullet>
        <Bullet positive>&ldquo;Let&rsquo;s take this slower.&rdquo;</Bullet>
        <p className="text-[13px] font-[800] mt-3 mb-1" style={{ color: '#B91C1C' }}>Signs your boundaries aren&rsquo;t respected:</p>
        <Bullet>The other person gets angry, sulky, or defensive when you say no.</Bullet>
        <Bullet>They keep asking after you&rsquo;ve declined.</Bullet>
        <Bullet>They joke about, mock, or minimize your limits.</Bullet>
        <Callout tone="warning">A &ldquo;no&rdquo; that&rsquo;s ignored isn&rsquo;t romantic. It&rsquo;s a warning.</Callout>
      </TopicCard>

      <TopicCard number={3} icon={ShieldCheck} gradient="linear-gradient(135deg, #7E22CE, #1E4FD8)" title="Respecting Consent">
        <P>Consent is freely given, reversible, informed, enthusiastic, and specific.</P>
        <Bullet><b>Freely given:</b> not obtained through pressure, guilt, or intoxication.</Bullet>
        <Bullet><b>Reversible:</b> can be withdrawn at any moment.</Bullet>
        <Bullet><b>Informed:</b> both people understand what they&rsquo;re agreeing to.</Bullet>
        <Bullet><b>Enthusiastic:</b> a maybe or a silence is a no.</Bullet>
        <Bullet><b>Specific:</b> yes to one thing isn&rsquo;t yes to everything.</Bullet>
      </TopicCard>

      <TopicCard number={4} icon={MessageCircle} gradient="linear-gradient(135deg, #0EA5E9, #1E4FD8)" title="Healthy Communication">
        <Bullet positive>Say what you feel, not what the other person did wrong.</Bullet>
        <Bullet positive>Ask questions before making assumptions.</Bullet>
        <Bullet positive>Repeat back what you heard &mdash; misunderstandings shrink fast.</Bullet>
        <Bullet positive>Take breaks when a conversation heats up.</Bullet>
        <Bullet positive>Follow through on what you commit to.</Bullet>
        <P>Great communication is not about winning &mdash; it&rsquo;s about understanding.</P>
      </TopicCard>

      <TopicCard number={5} icon={TrendingDown} gradient="linear-gradient(135deg, #DC2626, #B91C1C)" title="Handling Rejection">
        <P>Rejection is a normal, healthy part of dating. It reflects fit &mdash; not worth.</P>
        <Bullet>Give yourself permission to feel the sting.</Bullet>
        <Bullet>Resist the urge to send a long &ldquo;why?&rdquo; message.</Bullet>
        <Bullet>Don&rsquo;t escalate, insult, or negotiate.</Bullet>
        <Bullet>Take a break if you need to &mdash; re-open the app when you&rsquo;re ready.</Bullet>
        <Callout tone="info">Compatibility is a discovery, not a competition.</Callout>
      </TopicCard>

      <TopicCard number={6} icon={Hourglass} gradient="linear-gradient(135deg, #16A34A, #0EA5E9)" title="Taking It at a Comfortable Pace">
        <P>Meet to Keep is built for intentional relationships. That means letting depth grow at its own pace.</P>
        <Bullet>You don&rsquo;t owe anyone escalation.</Bullet>
        <Bullet>You don&rsquo;t owe anyone exclusivity on their timeline.</Bullet>
        <Bullet>You don&rsquo;t owe anyone a meeting, a photo, or a kiss.</Bullet>
        <Bullet positive>You <b>do</b> owe honesty about where you&rsquo;re at.</Bullet>
        <Callout tone="success">The best relationships tend to grow &mdash; they aren&rsquo;t forced.</Callout>
      </TopicCard>

      <p className="text-[12px] italic text-center mt-6 mb-2" style={{ color: '#6B7280' }}>
        Adapted from mental-health, relationships, and dating-safety literature.
      </p>

      <LegalFooter />
    </LegalPageShell>
  );
}

/* ------------------------------------------------------------ */
/* 7. Verified Profile                                          */
/* ------------------------------------------------------------ */
export function SafetyVerification() {
  const navigate = useNavigate();
  return (
    <LegalPageShell title="Verification" backTo="/safety">
      <HeroCard icon={CheckCircle2} title="Verified Profile" />
      <p className="text-center text-[13px] font-[600] text-[#6B7280] -mt-4 mb-4">
        What the badge means, what it doesn&rsquo;t, and how to earn it.
      </p>

      {/* Badge preview */}
      <div className="flex justify-center mb-4" data-testid="verification-badge-preview">
        <span
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-white text-[13px] font-[900]"
          style={{ background: 'linear-gradient(135deg, #16A34A, #0EA5E9)', boxShadow: '0 4px 10px -2px rgba(22,163,74,0.35)' }}
        >
          <BadgeCheck size={14} strokeWidth={2.4} />
          Verified
        </span>
      </div>

      {/* Card 1 */}
      <TopicCard number={1} icon={CheckCircle2} gradient="linear-gradient(135deg, #16A34A, #0EA5E9)" title="What verification means">
        <P>A verification badge shows that this account has completed our identity-verification process by matching a real-time selfie to their profile photos.</P>
        <Bullet positive>The person shown in the photos is likely the same person using the account.</Bullet>
        <Bullet positive>The account has passed automated liveness and face-match checks.</Bullet>
        <Bullet positive>The verification was successful at the time it was completed.</Bullet>
      </TopicCard>

      {/* Card 2 — red-tinted border */}
      <section className="mt-6" data-testid="safety-topic-2">
        <div
          className="rounded-2xl p-4"
          style={{ background: '#FFFFFF', border: '1px solid rgba(220,38,38,0.25)' }}
        >
          <div className="flex items-center gap-2.5 mb-3">
            <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: '#FEE2E2' }}>
              <XCircle size={16} className="text-[#B91C1C]" strokeWidth={2.4}/>
            </span>
            <h3 className="text-[17px] font-[900] text-[#0F0F10]" style={{ letterSpacing: '-0.3px' }}>
              What it does NOT mean
            </h3>
          </div>
          <Bullet><b>Not a background check.</b> We don&rsquo;t confirm criminal history.</Bullet>
          <Bullet><b>Not an identity check.</b> We don&rsquo;t confirm legal name.</Bullet>
          <Bullet><b>Not a guarantee</b> of intentions, honesty, or safety.</Bullet>
          <Bullet><b>Not permanent.</b> Accounts can be re-flagged for re-verification.</Bullet>
          <Callout tone="warning">Continue using good judgment in every interaction &mdash; verified or not.</Callout>
        </div>
      </section>

      {/* Card 3 */}
      <TopicCard number={3} icon={Sparkles} gradient="linear-gradient(135deg, #7E22CE, #1E4FD8)" title="How to get verified">
        <Bullet positive><b>Step 1.</b> Open Settings and tap &ldquo;Verify Account.&rdquo;</Bullet>
        <Bullet positive><b>Step 2.</b> Follow the on-screen prompts to take a live selfie in a well-lit space.</Bullet>
        <Bullet positive><b>Step 3.</b> We compare it to your profile photos in real time &mdash; usually seconds.</Bullet>
        <Bullet positive><b>Step 4.</b> If successful, the verified badge appears on your profile immediately.</Bullet>
        <P>If verification fails, retake with better lighting, remove sunglasses, and keep your face fully in frame.</P>
      </TopicCard>

      <button
        onClick={() => navigate('/verify')}
        data-testid="verification-go-settings"
        className="w-full flex items-center justify-center gap-2 rounded-full text-white font-[900] text-[15px] py-3.5 mt-6"
        style={{ background: 'linear-gradient(135deg, #16A34A, #0EA5E9)', boxShadow: '0 6px 20px -4px rgba(22,163,74,0.45)' }}
      >
        <BadgeCheck size={16} />
        Verify My Account
      </button>

      <p className="text-[12px] italic text-center mt-6 mb-2" style={{ color: '#6B7280' }}>
        Verification supports safety &mdash; it doesn&rsquo;t replace it.
      </p>

      <LegalFooter />
    </LegalPageShell>
  );
}

/* ------------------------------------------------------------ */
/* 8. Connection Credibility                                    */
/* ------------------------------------------------------------ */
function WeightDots({ level }) {
  return (
    <div className="flex items-center gap-1">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="w-1.5 h-1.5 rounded-full"
          style={{ background: i < level ? '#7E22CE' : 'rgba(126,34,206,0.15)' }}
        />
      ))}
    </div>
  );
}

export function SafetyCredibility() {
  const factors = [
    { icon: MessageCircle, label: 'Responsiveness to matches',            weight: 3 },
    { icon: CalendarClock, label: 'Follow-through on plans',              weight: 3 },
    { icon: ShieldCheck,   label: 'Verified profile status',              weight: 2 },
    { icon: Flag,          label: 'Number of legitimate reports against the account', weight: 3 },
    { icon: Users,         label: 'Profile completeness',                 weight: 2 },
    { icon: Clock,         label: 'Account age & consistency',            weight: 1 },
    { icon: AlertTriangle, label: 'Guideline violations or moderation actions', weight: 3 },
  ];
  return (
    <LegalPageShell title="Credibility" backTo="/safety">
      <HeroCard icon={Award} title="Connection Credibility" />
      <p className="text-center text-[13px] font-[600] text-[#6B7280] -mt-4 mb-4">
        How the score works &mdash; and its limits.
      </p>

      <TopicCard number={1} icon={Star} gradient="linear-gradient(135deg, #F59E0B, #F97316)" title="What it is">
        <P>Connection Credibility is a lightweight signal designed to give you extra context about how someone tends to show up on Meet to Keep.</P>
        <P>It reflects patterns like reliability, responsiveness, and community behavior &mdash; not identity, not looks, not popularity.</P>
      </TopicCard>

      <TopicCard number={2} icon={Star} gradient="linear-gradient(135deg, #F59E0B, #DC2626)" title="What influences it">
        <div className="space-y-2 mt-2">
          {factors.map((f, i) => {
            const Icon = f.icon;
            return (
              <div
                key={i}
                className="flex items-center gap-3 bg-white border border-[rgba(15,15,16,0.06)] rounded-xl p-3"
                data-testid={`credibility-factor-${i}`}
              >
                <span className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#F4F4F5' }}>
                  <Icon size={15} className="text-[#4B4B52]" strokeWidth={2}/>
                </span>
                <span className="flex-1 text-[13px] font-[600] text-[#0F0F10]">{f.label}</span>
                <WeightDots level={f.weight} />
              </div>
            );
          })}
        </div>
        <p className="text-[12px] italic mt-4" style={{ color: '#6B7280', lineHeight: '18px' }}>
          We intentionally don&rsquo;t publish the exact formula &mdash; detailed weights and thresholds would let bad actors game the system.
        </p>
      </TopicCard>

      {/* Card 3 — red-tinted */}
      <section className="mt-6" data-testid="safety-topic-3">
        <div
          className="rounded-2xl p-4"
          style={{ background: '#FFFFFF', border: '1px solid rgba(220,38,38,0.25)' }}
        >
          <div className="flex items-center gap-2.5 mb-3">
            <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: '#FEE2E2' }}>
              <XCircle size={16} className="text-[#B91C1C]" strokeWidth={2.4}/>
            </span>
            <h3 className="text-[17px] font-[900] text-[#0F0F10]" style={{ letterSpacing: '-0.3px' }}>
              What it does NOT guarantee
            </h3>
          </div>
          <Bullet>It is not a background check.</Bullet>
          <Bullet>It is not a personality score.</Bullet>
          <Bullet>It does not verify someone&rsquo;s intentions or honesty.</Bullet>
          <Bullet>It cannot predict how safe or compatible a person will be for you.</Bullet>
          <Callout tone="warning">Use Credibility as one signal among many &mdash; never as a substitute for your judgment.</Callout>
        </div>
      </section>

      <p className="text-[12px] italic text-center mt-6 mb-2" style={{ color: '#6B7280' }}>
        Credibility is a signal. You are the decision-maker.
      </p>

      <LegalFooter />
    </LegalPageShell>
  );
}

/* ------------------------------------------------------------ */
/* 9. AI Safety Assistant                                       */
/* ------------------------------------------------------------ */
export function SafetyAIAssistant() {
  const navigate = useNavigate();
  const questions = [
    { icon: MessageCircle,  q: 'Is this message a scam?',            desc: 'Guides you through common scam signals in the exact text you paste.' },
    { icon: Flag,           q: 'Is this behavior a red flag?',       desc: 'Breaks down patterns of concerning behavior versus healthy dynamics.' },
    { icon: HelpCircle,     q: 'Should I meet this person?',         desc: 'Walks you through the decision — without deciding for you.' },
    { icon: CalendarClock,  q: 'How do I plan a safe first date?',   desc: 'Suggests venues, timing, transportation, and check-in strategies.' },
    { icon: ShieldHalf,     q: 'What are my options if I feel unsafe?', desc: 'Points you to the right combination of block, report, and support.' },
  ];
  const principles = [
    { icon: Lock,          title: 'Private by default',          body: "Conversations aren't linked to your dating profile." },
    { icon: HandMetal,     title: 'Guidance, not verdicts',      body: 'It points to warning signs and options — not judgments about people.' },
    { icon: AlertCircle,   title: 'Always escalates',            body: 'If danger is mentioned, it points you to emergency resources first.' },
    { icon: GraduationCap, title: 'Trained on safety literature', body: 'Grounded in scam-prevention and relationship-safety research.' },
  ];
  return (
    <LegalPageShell title="AI Safety Assistant" backTo="/safety">
      <HeroCard icon={Sparkles} title="AI Safety Assistant" />
      <p className="text-center text-[13px] font-[600] text-[#6B7280] -mt-4 mb-3">
        A private, non-judgmental helper for messy situations.
      </p>

      {/* Coming Soon chip */}
      <div className="flex justify-center mb-4" data-testid="ai-coming-soon-chip">
        <span
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-[900] uppercase"
          style={{ background: 'rgba(126,34,206,0.1)', color: '#7E22CE', border: '1px solid rgba(126,34,206,0.25)', letterSpacing: '0.6px' }}
        >
          <Clock size={12} strokeWidth={2.4}/>
          Coming Soon
        </span>
      </div>

      <P>The AI Safety Assistant will let you ask sensitive questions in private &mdash; without judgment, and without recording your private conversations to your profile.</P>

      {/* Sample questions */}
      <p className="text-[11.5px] font-[900] uppercase mt-6 mb-3" style={{ color: '#6B7280', letterSpacing: '0.8px' }}>
        What you&rsquo;ll be able to ask
      </p>
      <div className="space-y-2.5" data-testid="ai-sample-questions">
        {questions.map((r, i) => {
          const Icon = r.icon;
          return (
            <div
              key={i}
              className="flex items-start gap-2.5 rounded-xl p-3 bg-white"
              style={{ border: '1px solid rgba(126,34,206,0.15)' }}
            >
              <span className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(126,34,206,0.1)' }}>
                <Icon size={15} className="text-[#7E22CE]" strokeWidth={2}/>
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-[13.5px] font-[700] italic text-[#0F0F10]">&ldquo;{r.q}&rdquo;</p>
                <p className="text-[12px] text-[#6B7280] mt-0.5" style={{ lineHeight: '17px' }}>{r.desc}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* How it works */}
      <p className="text-[11.5px] font-[900] uppercase mt-6 mb-3" style={{ color: '#6B7280', letterSpacing: '0.8px' }}>
        How it will work
      </p>
      <div
        className="rounded-2xl p-3.5 bg-white space-y-3.5"
        style={{ border: '1px solid rgba(15,15,16,0.06)' }}
        data-testid="ai-principles"
      >
        {principles.map((p, i) => {
          const Icon = p.icon;
          return (
            <div key={i} className="flex items-start gap-3">
              <span className="w-[30px] h-[30px] rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#0F0F10' }}>
                <Icon size={14} className="text-white" strokeWidth={2}/>
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-[13.5px] font-[900] text-[#0F0F10]">{p.title}</p>
                <p className="text-[12px] text-[#6B7280]" style={{ lineHeight: '16px' }}>{p.body}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4">
        <Callout tone="warning">AI can make mistakes and cannot determine another person&rsquo;s true intentions. Use it as a second opinion, never as a substitute for your own judgment or professional help.</Callout>
      </div>

      <button
        onClick={() => navigate('/safety/tips')}
        data-testid="ai-waitlist"
        className="w-full flex items-center justify-center gap-2 rounded-full text-white font-[900] text-[15px] py-3.5 mt-2"
        style={{ background: 'linear-gradient(135deg, #8B5CF6, #1E4FD8)', boxShadow: '0 6px 20px -4px rgba(139,92,246,0.45)' }}
      >
        <Lightbulb size={16} />
        Read the Safety Tips while you wait
      </button>

      <p className="text-[12px] italic text-center mt-6 mb-2" style={{ color: '#6B7280' }}>
        Announcing soon. If it can&rsquo;t be safe &amp; private, we won&rsquo;t ship it.
      </p>

      <LegalFooter />
    </LegalPageShell>
  );
}
